import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronRight,
  ExternalLink,
  FileText,
  Inbox as InboxIcon,
  Link2,
  MessagesSquare,
  Paperclip,
  RefreshCw,
  Send,
  ShoppingBag,
  StickyNote,
  Trash2,
  UserPlus,
} from 'lucide-react';
import {
  DEFAULT_DUE_TIME,
  TIME_SLOTS,
  formatMoney,
  relativeTime,
  renderableEmoji,
  slotLabel,
  todayLocal,
  type ChannelType,
  type ConversationStatus,
  type MessageAttachment,
} from '@bakery/shared';

import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';

const CHANNEL_LABEL: Record<string, string> = {
  messenger: 'Messenger',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  viber: 'Viber',
  tiktok: 'TikTok',
  sms: 'SMS',
  email: 'Email',
  walk_in: 'Walk-in',
};

/** Meta's own ceiling is higher; this one fails fast on a phone connection. */
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

/**
 * When each thread was last read back from Messenger.
 *
 * Opening a thread checks Meta for what the webhook missed, and clicking
 * between threads would otherwise call out every time. Kept in memory on
 * purpose: a page reload should check again.
 */
const lastPulled = new Map<string, number>();
const PULL_EVERY_MS = 60_000;

/**
 * A file as base64, without the data: prefix.
 *
 * Via FileReader rather than a loop over the bytes: building a binary string
 * with String.fromCharCode spread over a few million characters overruns the
 * call stack, and a shop photographing a cake will comfortably reach that.
 */
function base64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(file);
  });
}

export function InboxScreen() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ConversationStatus>('open');

  const list = useQuery({
    queryKey: ['conversations', status],
    queryFn: () => api.inbox.conversations({ status }),
    refetchInterval: 20_000,
  });

  const channels = useQuery({ queryKey: ['channels'], queryFn: api.inbox.channels });
  const connectedCount = channels.data?.filter((c) => c.channel?.status === 'connected').length ?? 0;

  return (
    <div className="flex h-full">
      {/* Thread list ------------------------------------------------------ */}
      <section
        className={cx(
          'flex w-full flex-col border-r border-[var(--color-line)] lg:w-[360px]',
          conversationId && 'hidden lg:flex',
        )}
      >
        <header className="space-y-3 border-b border-[var(--color-line)] px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-lg font-semibold">Inbox</h1>
            <Badge tone={connectedCount > 0 ? 'ok' : 'warn'}>
              {connectedCount} channel{connectedCount === 1 ? '' : 's'} live
            </Badge>
          </div>
          <Tabs
            value={status}
            onChange={setStatus}
            tabs={[
              { value: 'open' as const, label: 'Open', count: list.data?.counts.open },
              { value: 'pending' as const, label: 'Pending', count: list.data?.counts.pending },
              { value: 'resolved' as const, label: 'Resolved' },
            ]}
          />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {list.isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-16" />
              ))}
            </div>
          ) : list.data?.conversations.length === 0 ? (
            <EmptyState
              icon={<InboxIcon className="size-8" />}
              title="No conversations"
              description={
                connectedCount === 0
                  ? 'Connect Messenger, WhatsApp or Viber in Settings and messages will land here.'
                  : 'Nothing waiting in this folder.'
              }
            />
          ) : (
            <ul className="divide-y divide-[var(--color-line)]">
              {list.data?.conversations.map((conversation) => (
                <li key={conversation.id}>
                  <button
                    onClick={() => navigate(`/inbox/${conversation.id}`)}
                    className={cx(
                      'w-full px-4 py-3 text-left transition hover:bg-[var(--color-surface-sunken)]',
                      conversationId === conversation.id && 'bg-[var(--color-accent-soft)]',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium">{conversation.contactName}</span>
                      <span className="shrink-0 text-[11px] text-[var(--color-ink-faint)]">
                        {relativeTime(conversation.lastMessageAt)}
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-[var(--color-ink-soft)]">
                      {conversation.lastMessagePreview}
                    </p>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Badge tone="neutral">{CHANNEL_LABEL[conversation.channel] ?? conversation.channel}</Badge>
                      <span className="tabular text-[11px] text-[var(--color-ink-faint)]">{conversation.ticketNo}</span>
                      {conversation.unreadCount > 0 && <Badge tone="brand">{conversation.unreadCount} new</Badge>}
                      {conversation.orderId && <Badge tone="info">Order</Badge>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Thread ----------------------------------------------------------- */}
      <section className={cx('min-w-0 flex-1', !conversationId && 'hidden lg:block')}>
        {conversationId ? (
          <Thread conversationId={conversationId} onBack={() => navigate('/inbox')} />
        ) : (
          <EmptyState
            icon={<MessagesSquare className="size-10" />}
            title="Pick a conversation"
            description="Replies, customer history and creating an order all happen here."
          />
        )}
      </section>
    </div>
  );
}

/**
 * One attachment in a thread.
 *
 * Images show themselves; everything else is a link. The URLs Meta sends are
 * signed and expire after a while, so an old photo eventually stops loading —
 * the fallback says so rather than leaving a broken frame, because "this
 * expired" and "this never arrived" are very different problems for staff.
 */
function Attachment({
  attachment,
  outbound,
  channel,
}: {
  attachment: MessageAttachment;
  outbound?: boolean;
  channel?: string;
}) {
  const [broken, setBroken] = useState(false);
  const label = attachment.name ?? attachment.type;

  if (!attachment.url) {
    /*
     * Three reasons for a missing URL, and they read very differently to
     * whoever is looking at the thread.
     *
     * One we sent ourselves: the bytes went straight to the platform and were
     * deliberately not kept, so the record is the filename. A card Messenger
     * drew — a payment request, a transfer — which this app is told nothing
     * about beyond its existence. Or WhatsApp handing over a media id rather
     * than a link, leaving the file inside WhatsApp.
     */
    const where = CHANNEL_LABEL[channel ?? ''] ?? 'the app';
    return (
      <p className="flex items-center gap-1.5 text-xs opacity-80">
        <Paperclip className="size-3.5 shrink-0" />
        {outbound ? label : `${label} — open it in ${where}`}
      </p>
    );
  }

  if ((attachment.type === 'image' || attachment.type === 'sticker') && !broken) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="block">
        <img
          src={attachment.url}
          alt={label}
          loading="lazy"
          onError={() => setBroken(true)}
          className={cx(
            'max-h-64 w-auto rounded-[var(--radius-md)] object-cover',
            attachment.type === 'sticker' && 'max-h-28',
          )}
        />
      </a>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1.5 text-xs underline underline-offset-2 opacity-90 hover:opacity-100"
    >
      <Paperclip className="size-3.5 shrink-0" />
      {broken ? `${label} — the link has expired` : label}
    </a>
  );
}

function Thread({ conversationId, onBack }: { conversationId: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const conversation = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.inbox.conversation(conversationId),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.data?.messages.length]);

  const reply = useMutation({
    mutationFn: () => api.inbox.reply(conversationId, { body: draft, isPrivateNote: isNote }),
    onSuccess: (result) => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      if ('delivered' in result && !result.delivered) {
        // Not connected, or the platform has no API. The text is saved either
        // way, and the operator gets a one-tap link to finish in the real app.
        toast.push({
          tone: 'warn',
          title: 'Saved, but not delivered',
          body: result.error ?? undefined,
          action: result.handoffUrl ? { label: 'Open the app to send', href: result.handoffUrl } : undefined,
        });
      }
    },
  });

  /**
   * Read the thread back from Messenger.
   *
   * Runs once when a thread is opened, quietly. Messenger writes messages of
   * its own into a thread — a payment transfer request is one — and those never
   * reach the webhook, so without this a customer can be asked to pay, and pay,
   * with no trace of it here.
   */
  const pullMissing = useMutation({
    mutationFn: () => {
      lastPulled.set(conversationId, Date.now());
      return api.inbox.syncConversation(conversationId);
    },
    onSuccess: (result) => {
      if (result.added > 0) {
        queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
    },
    // A thread that cannot be read back is not an error worth interrupting
    // anyone over: the messages that did arrive are all still here.
    onError: () => {},
  });

  const canPull = conversation.data?.channel === 'messenger' || conversation.data?.channel === 'instagram';

  useEffect(() => {
    const since = Date.now() - (lastPulled.get(conversationId) ?? 0);
    if (canPull && since > PULL_EVERY_MS) pullMissing.mutate();
    // Deliberately not depending on the mutation: it is recreated every render,
    // and listing it here would call Meta in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, canPull]);

  const sendFile = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        throw new Error(`${file.name} is ${Math.round(file.size / 1024 / 1024)} MB. The limit is 8 MB.`);
      }
      return api.inbox.sendAttachment(conversationId, {
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        data: await base64(file),
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      if (!result.delivered) {
        // Recorded in the thread either way, so nobody sends it twice blind.
        toast.push({
          tone: 'warn',
          title: 'Saved, but not delivered',
          body: result.error ?? undefined,
          action: result.handoffUrl ? { label: 'Open the app to send', href: result.handoffUrl } : undefined,
        });
      }
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not send that file', body: error.message }),
  });

  const linkCustomer = useMutation({
    mutationFn: () => api.inbox.linkCustomer(conversationId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      toast.push({ tone: 'ok', title: `${result.customer.displayName} added to clients` });
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: ConversationStatus) => api.inbox.update(conversationId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  if (conversation.isLoading) {
    return <div className="p-4"><Skeleton className="h-full min-h-64" /></div>;
  }

  /*
   * A thread that is not there any more.
   *
   * This used to sit on the loading skeleton for ever, which reads as a slow
   * network rather than a dead link — and it is now reachable, because an
   * order links back to the conversation it came from and that id can outlive
   * the thread, or arrive from a device whose messages have not synced yet.
   */
  if (!conversation.data) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <EmptyState
          icon={<MessagesSquare className="size-8" />}
          title="This conversation is not here"
          description="It may have been removed, or it may belong to a device that has not synced yet."
          action={<Button onClick={onBack}>Back to the inbox</Button>}
        />
      </div>
    );
  }

  const data = conversation.data;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--color-line)] px-4 py-3">
        <Button size="sm" variant="ghost" className="lg:hidden" onClick={onBack}>
          Back
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{data.contactName}</p>
          <p className="text-xs text-[var(--color-ink-soft)]">
            {CHANNEL_LABEL[data.channel] ?? data.channel} · {data.ticketNo}
            {data.customerDisplayName && ` · ${data.customerDisplayName}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {!data.customerId && (
            <Button size="sm" icon={<UserPlus className="size-4" />} loading={linkCustomer.isPending} onClick={() => linkCustomer.mutate()}>
              Add as client
            </Button>
          )}
          <Button size="sm" variant="primary" icon={<ShoppingBag className="size-4" />} onClick={() => setOrderOpen(true)}>
            Create order
          </Button>
          <Select
            value={data.status}
            onChange={(event) => setStatus.mutate(event.target.value as ConversationStatus)}
            className="h-9 w-32"
          >
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </Select>
        </div>
      </header>

      {data.linkedOrder && (
        /*
         * The banner is the way to the order, so it is a button rather than a
         * label. Staff were reading the order number off it and then going to
         * find it by hand on the board.
         */
        <button
          type="button"
          onClick={() => navigate(`/orders/${data.linkedOrder!.id}`)}
          className="flex w-full items-center gap-2 border-b border-[var(--color-line)] bg-[var(--color-info-soft)] px-4 py-2 text-left text-sm transition hover:brightness-95"
        >
          <Link2 className="size-4 shrink-0 text-[var(--color-info)]" />
          <span className="min-w-0 flex-1 truncate">
            Linked to <strong>{data.linkedOrder.orderNo}</strong> — {formatMoney(data.linkedOrder.total)},{' '}
            {data.linkedOrder.status.name}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 text-xs text-[var(--color-info)]">
            Open order <ChevronRight className="size-3.5" />
          </span>
        </button>
      )}

      {/* Messages -------------------------------------------------------- */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {data.messages.map((message) => (
          <div
            key={message.id}
            className={cx('flex', message.direction === 'out' && !message.isPrivateNote ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cx(
                'max-w-[75%] rounded-[var(--radius-lg)] px-3.5 py-2.5 text-sm',
                message.isPrivateNote
                  ? 'border border-dashed border-[var(--color-warn)]/50 bg-[var(--color-warn-soft)] text-[var(--color-ink)]'
                  : message.direction === 'out'
                    ? 'bg-crust-600 text-white'
                    : 'bg-[var(--color-surface-sunken)]',
              )}
            >
              {message.isPrivateNote && (
                <p className="mb-1 flex items-center gap-1 text-[11px] font-medium opacity-70">
                  <StickyNote className="size-3" /> Internal note
                </p>
              )}
              {/*
                * break-words as well as pre-wrap. Wrapping at whitespace alone
                * leaves a pasted tracking link — which has none — to run past
                * the bubble and drag a horizontal scrollbar across the whole
                * thread.
                */}
              {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}

              {message.attachments.length > 0 && (
                <div className={cx('space-y-1.5', message.body && 'mt-2')}>
                  {message.attachments.map((attachment, index) => (
                    <Attachment key={`${attachment.url}-${index}`} attachment={attachment} outbound={message.direction === 'out'} channel={data.channel} />
                  ))}
                </div>
              )}

              <p className={cx('mt-1 text-[11px]', message.direction === 'out' && !message.isPrivateNote ? 'text-white/60' : 'text-[var(--color-ink-faint)]')}>
                {relativeTime(message.sentAt)}
                {message.deliveryStatus === 'failed' && ' · not delivered'}
              </p>

              {/*
                * Reactions sit under the bubble as Facebook puts them, and are
                * read-only: Meta reports what a customer reacts with and gives
                * no way to react back from here.
                */}
              {message.reactions.length > 0 && (
                <div
                  className="-mb-3 mt-1 flex w-fit items-center gap-0.5 rounded-full border border-[var(--color-line)] bg-[var(--color-surface)] px-1.5 py-0.5 elevation-raised"
                  title={message.reactions.map((r) => r.name ?? r.emoji).join(', ')}
                >
                  {message.reactions.map((reaction, index) => (
                    <span key={`${reaction.actorId}-${index}`} className="text-sm leading-none">
                      {/*
                        * Also applied here, not only on the way in. Reactions
                        * stored before that fix landed are still bare code
                        * points in the database, and would go on drawing as
                        * an empty pill until someone reacted again.
                        */}
                      {renderableEmoji(reaction.emoji)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>

      {/* Composer -------------------------------------------------------- */}
      <footer className="space-y-2 border-t border-[var(--color-line)] px-4 py-3 pb-safe">
        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={isNote} onChange={(event) => setIsNote(event.target.checked)} className="size-3.5 accent-[var(--color-warn)]" />
            Internal note
          </label>

          {/*
            * The saved replies already existed on the server with no way to
            * reach them. A shop answering the same question forty times a day
            * should not be retyping the opening hours.
            */}
          <button
            type="button"
            onClick={() => setTemplatesOpen(true)}
            className="inline-flex items-center gap-1 text-crust-600 hover:underline"
          >
            <FileText className="size-3" /> Templates
          </button>

          {/*
            * Sending a picture back. An internal note is staff-only and never
            * leaves the building, so there is nothing to attach it to.
            */}
          {!isNote && (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={sendFile.isPending}
              className="inline-flex items-center gap-1 text-crust-600 hover:underline disabled:opacity-50"
            >
              <Paperclip className="size-3" /> {sendFile.isPending ? 'Sending…' : 'Attach'}
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared first: picking the same file twice in a row fires no
              // change event otherwise, and the second send silently does
              // nothing.
              event.target.value = '';
              if (file) sendFile.mutate(file);
            }}
          />

          {canPull && (
            <button
              type="button"
              onClick={() =>
                pullMissing.mutate(undefined, {
                  onSuccess: (result) =>
                    toast.push(
                      result.added > 0
                        ? { tone: 'ok', title: `${result.added} message${result.added === 1 ? '' : 's'} recovered` }
                        : { tone: 'neutral', title: 'Nothing missing — this thread is complete' },
                    ),
                  onError: () =>
                    toast.push({ tone: 'warn', title: 'Messenger would not hand over this thread just now' }),
                })
              }
              disabled={pullMissing.isPending}
              className="inline-flex items-center gap-1 text-crust-600 hover:underline disabled:opacity-50"
            >
              <RefreshCw className={cx('size-3', pullMissing.isPending && 'animate-spin')} />
              {pullMissing.isPending ? 'Checking…' : 'Check Messenger'}
            </button>
          )}

          {data.handoffUrl && (
            <a href={data.handoffUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-crust-600 hover:underline">
              <ExternalLink className="size-3" /> Open in {CHANNEL_LABEL[data.channel]}
            </a>
          )}
        </div>

        <div className="flex gap-2">
          <Textarea
            rows={2}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends; Shift+Enter makes a new line, as in every chat app.
              if (event.key === 'Enter' && !event.shiftKey && draft.trim()) {
                event.preventDefault();
                reply.mutate();
              }
            }}
            onPaste={(event) => {
              /*
               * A screenshot usually arrives on the clipboard, not as a file on
               * disk, and having to save it first to attach it is the kind of
               * detour that sends people back to Messenger.
               */
              if (isNote) return;
              const file = [...event.clipboardData.files][0];
              if (!file) return;
              event.preventDefault();
              sendFile.mutate(file);
            }}
            placeholder={isNote ? 'Note for the team, not sent to the customer…' : 'Write a reply…'}
            className="flex-1 resize-none"
          />
          <Button
            variant="primary"
            className="self-end"
            icon={<Send className="size-4" />}
            loading={reply.isPending}
            disabled={!draft.trim()}
            onClick={() => reply.mutate()}
          >
            Send
          </Button>
        </div>
      </footer>

      {orderOpen && <OrderFromChatModal conversationId={conversationId} onClose={() => setOrderOpen(false)} />}

      {templatesOpen && (
        <TemplatePicker
          onClose={() => setTemplatesOpen(false)}
          onPick={(body) => {
            // Appended, not replaced: half a reply may already be typed.
            setDraft((current) => (current.trim() ? `${current.trimEnd()}\n\n${body}` : body));
            setTemplatesOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** Saved replies, for the questions a bakery answers forty times a day. */
function TemplatePicker({ onClose, onPick }: { onClose: () => void; onPick: (body: string) => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: '', body: '' });

  const replies = useQuery({ queryKey: ['canned-replies'], queryFn: api.inbox.cannedReplies });

  const create = useMutation({
    mutationFn: () => api.inbox.createCannedReply({ title: draft.title, body: draft.body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canned-replies'] });
      setDraft({ title: '', body: '' });
      setAdding(false);
      toast.push({ tone: 'ok', title: 'Template saved' });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.inbox.deleteCannedReply(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['canned-replies'] }),
  });

  const term = search.trim().toLowerCase();
  const matches = (replies.data ?? []).filter(
    (r) => !term || r.title.toLowerCase().includes(term) || r.body.toLowerCase().includes(term),
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Templates"
      description="Saved replies. Picking one drops it into the box so it can still be edited before sending."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="secondary" onClick={() => setAdding((current) => !current)}>
            {adding ? 'Cancel' : 'New template'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {adding ? (
          <div className="space-y-3">
            <Field label="Title" required>
              <Input
                value={draft.title}
                onChange={(event) => setDraft((c) => ({ ...c, title: event.target.value }))}
                placeholder="Opening hours"
                autoFocus
              />
            </Field>
            <Field label="Message" required>
              <Textarea
                rows={5}
                value={draft.body}
                onChange={(event) => setDraft((c) => ({ ...c, body: event.target.value }))}
                placeholder="We are open Monday to Friday, 9 AM to 6 PM."
              />
            </Field>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!draft.title.trim() || !draft.body.trim()}
              onClick={() => create.mutate()}
            >
              Save template
            </Button>
          </div>
        ) : (
          <>
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search templates…" autoFocus />

            {replies.isLoading ? (
              <Skeleton className="h-32" />
            ) : matches.length === 0 ? (
              <p className="py-6 text-center text-sm text-[var(--color-ink-faint)]">
                {replies.data?.length ? 'Nothing matches that.' : 'No templates saved yet.'}
              </p>
            ) : (
              <ul className="max-h-80 space-y-1.5 overflow-y-auto">
                {matches.map((template) => (
                  <li key={template.id} className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => onPick(template.body)}
                      className="min-w-0 flex-1 rounded-[var(--radius-md)] border border-[var(--color-line)] px-3 py-2 text-left hover:bg-[var(--color-surface-sunken)]"
                    >
                      <span className="block text-sm font-medium">{template.title}</span>
                      <span className="mt-0.5 line-clamp-2 block text-xs text-[var(--color-ink-soft)]">
                        {template.body}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      aria-label={`Delete ${template.title}`}
                      className="shrink-0 px-2 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10"
                      onClick={() => remove.mutate(template.id)}
                      icon={<Trash2 className="size-4" />}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/** Build an order without leaving the conversation. */
function OrderFromChatModal({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.catalog.products() });

  const [lines, setLines] = useState<{ productId: string; qty: number }[]>([]);
  const [fulfillment, setFulfillment] = useState('pickup');
  const [dueDate, setDueDate] = useState(todayLocal);
  const [dueTime, setDueTime] = useState(DEFAULT_DUE_TIME.pickup);

  /*
   * Switching between pickup and delivery moves the time to that kind's usual
   * hour — but only until somebody picks one themselves. Overwriting a chosen
   * time because the fulfilment changed would quietly lose a real decision.
   */
  const [timeChosen, setTimeChosen] = useState(false);

  const changeFulfillment = (value: string) => {
    setFulfillment(value);
    if (!timeChosen) setDueTime(DEFAULT_DUE_TIME[value] ?? DEFAULT_DUE_TIME.pickup);
  };

  const total = lines.reduce((sum, line) => {
    const product = products.data?.find((p) => p.id === line.productId);
    return sum + (product?.price ?? 0) * line.qty;
  }, 0);

  const create = useMutation({
    mutationFn: () =>
      api.inbox.createOrder(conversationId, {
        lines,
        fulfillment,
        // `2026-09-22T16:00` with no zone is read as local time, which is the
        // shop's clock — exactly what was picked.
        dueAt: dueDate && dueTime ? new Date(`${dueDate}T${dueTime}`).toISOString() : null,
      }),
    onSuccess: ({ order, notification }) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.push({
        tone: 'ok',
        title: `${order.orderNo} created`,
        body:
          notification?.status === 'sent'
            ? 'Order summary sent to the customer.'
            : (notification?.error ?? undefined),
        action: notification?.handoffUrl ? { label: 'Send the summary', href: notification.handoffUrl } : undefined,
      });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not create', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Order from this conversation"
      description="The customer, the channel and the thread are all linked automatically."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={lines.length === 0} onClick={() => create.mutate()}>
            Create {formatMoney(total)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-5">
          <Field label="Fulfilment" className="min-w-0 sm:col-span-2">
            <Select value={fulfillment} onChange={(event) => changeFulfillment(event.target.value)}>
              <option value="pickup">Pickup</option>
              <option value="delivery">Delivery</option>
            </Select>
          </Field>
          {/*
            * A date input carries its own intrinsic width and a grid item will
            * not shrink below its content, so an even split pushed the pair
            * past the edge of the dialog. This one gets the larger share, and
            * `min-w-0` lets it give way rather than overflow.
            */}
          <Field label="Needed by" className="min-w-0 sm:col-span-3">
            <div className="flex gap-2">
              <Input
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                className="min-w-0 flex-1"
              />
              <Select
                value={dueTime}
                onChange={(event) => {
                  setDueTime(event.target.value);
                  setTimeChosen(true);
                }}
                className="w-32 shrink-0"
              >
                {TIME_SLOTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slotLabel(slot)}
                  </option>
                ))}
              </Select>
            </div>
          </Field>
        </div>

        <div className="space-y-2">
          {lines.map((line, index) => (
            <div key={index} className="flex gap-2">
              <Select
                value={line.productId}
                onChange={(event) => setLines((c) => c.map((l, i) => (i === index ? { ...l, productId: event.target.value } : l)))}
                className="min-w-0 flex-1"
              >
                {products.data?.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} — {formatMoney(product.price)}
                  </option>
                ))}
              </Select>
              <Input
                type="number"
                min={1}
                value={line.qty}
                onChange={(event) => setLines((c) => c.map((l, i) => (i === index ? { ...l, qty: Number(event.target.value) } : l)))}
                className="tabular w-20 shrink-0 text-center"
              />
              <Button
                variant="ghost"
                aria-label={`Remove ${products.data?.find((p) => p.id === line.productId)?.name ?? 'this item'}`}
                title="Remove this item"
                className="shrink-0 px-2.5 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10"
                onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                icon={<Trash2 className="size-4" />}
              />
            </div>
          ))}
          <Button
            size="sm"
            icon={<ShoppingBag className="size-4" />}
            disabled={!products.data?.length}
            onClick={() => setLines((current) => [...current, { productId: products.data![0]!.id, qty: 1 }])}
          >
            Add an item
          </Button>
        </div>
      </div>
    </Modal>
  );
}
